import { Form, Formik } from "formik";
import { sectionsQuestions } from "pages/WorkersCompApplcation/utils/sectionsQuestions";
import React, { useState } from "react";
import styles from "../PrimaryBusinessAddress.module.css";
import FooterButton from "uicomponents/FooterButton";
import BussinessAddressComponent from "uicomponents/AddressComponent/BusinessAddressComponent";
import Alert from "uicomponents/Alert";
import {
  BACK_BUTTON_TEXT,
  LOADING_BUTTON_TEXT,
  NEXT_BUTTON_TEXT,
} from "pages/PreQualification/utils/constants";
import { BusinessAddressFormType } from "uicomponents/AddressComponent/FormUtils";
import { BusinessAddressInitialFormValues } from "uicomponents/AddressComponent/FormUtils";
import { SingleBusinessAddressType } from "types";
import { businessAddressValidationSchema } from "uicomponents/AddressComponent/FormUtils";
import { getProspectUuid } from "utils/idHelpers";
import { errorMessageMap } from "utils/helper";
import { trackFormError } from "utils/analytics/trackEvent";
import { useAddBussinessAddressMutation } from "types/generated/operations";
import { useUpdateBussinessAddressMutation } from "types/generated/operations";
import SuggestedAddressModel from "uicomponents/SuggestedAddressModel";
import InvalidDialog from "./InvalidDialog";

interface IPrimaryBusinessAddressFormsProps {
  data?: SingleBusinessAddressType;
  handleNextClick: () => void;
  disableNextStep?: boolean;
}

const PrimaryBusinessAddressForm = ({
  ...props
}: IPrimaryBusinessAddressFormsProps): JSX.Element => {
  const { data, handleNextClick } = props;

  const initialFormValues: BusinessAddressFormType =
    BusinessAddressInitialFormValues(data);

  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [invalidWarning, setInvalidWarning] = useState(false);
  const [suggestedAddresses, setSuggestedAddresses] = useState<
    BusinessAddressFormType[]
  >([]);

  const [addOfficeLocation, { loading: addBussinessAddressLoading }] =
    useAddBussinessAddressMutation();

  const [updateOfficeLocation, { loading: updateBussinessAddressLoading }] =
    useUpdateBussinessAddressMutation();

  const [createdUuid, setCreatedUuid] = useState("");

  const handleOnSubmit = async (values: BusinessAddressFormType) => {
    const queryBody = {
      address1: values.address1,
      address2: values.address2,
      city: values.city,
      zip: values.zip,
      state: values.state,
      locationName: values.locationName,
      isHq: true,
      subdivision: values.subdivision,
    };

    try {
      let response;
      let data;

      const officeUuid = values.uuid || createdUuid;
      if (officeUuid) {
        response = await updateOfficeLocation({
          variables: { uuid: officeUuid, ...queryBody },
        });
        data = response?.data?.updateOfficeLocation;
      } else {
        response = await addOfficeLocation({
          variables: { uuid: getProspectUuid(), ...queryBody },
        });
        data = response?.data?.createOfficeLocation;
      }

      if (data && data.autoValidated === false) {
        if (data.suggestedAddress) {
          setSuggestedAddresses([
            {
              locationName: values.locationName,
              address1: values.address1,
              address2: values.address2,
              city: values.city,
              zip: values.zip,
              state: values.state,
              uuid: data.uuid || "",
              subdivision: values.subdivision,
            },
            {
              locationName: values.locationName,
              address1: data.suggestedAddress?.address1,
              address2: data.suggestedAddress?.address2 || "",
              city: data.suggestedAddress?.city,
              zip: data.suggestedAddress?.zip,
              state: data.suggestedAddress?.state,
              uuid: data.uuid || "",
              subdivision: values.subdivision,
            },
          ]);
        } else {
          setSuggestedAddresses([]);
          setInvalidWarning(true);
        }

        setCreatedUuid(data.uuid || "");
        return;
      }
      handleNextClick();
    } catch (error: any) {
      const errorMessage: string = error?.message;
      errorMessageMap[errorMessage] &&
        setErrorMessage(errorMessageMap[errorMessage]);
      trackFormError(error, errorMessage);
      setIsError(true);
      return;
    }
  };

  const handleKeepEntered = () => {
    setSuggestedAddresses([]);
    handleNextClick();
  };

  return (
    <>
      <InvalidDialog
        isOpen={invalidWarning}
        onContinue={() => {
          setInvalidWarning(false);
          handleNextClick();
        }}
        onReview={() => setInvalidWarning(false)}
      />

      {suggestedAddresses.length > 0 && (
        <SuggestedAddressModel
          enteredAddress={suggestedAddresses[0]}
          suggestedAddress={suggestedAddresses[1]}
          onUseSuggestion={handleOnSubmit}
          onKeepCurrent={handleKeepEntered}
        />
      )}
      <div className={styles.title}>
        {sectionsQuestions["officeLocation"].editCardTitlePrimary}
      </div>
      <div className={styles.description}>
        {sectionsQuestions["officeLocation"].openEditCardPrimaryDescription}
      </div>
      <Formik
        initialValues={initialFormValues}
        validationSchema={businessAddressValidationSchema}
        onSubmit={handleOnSubmit}
      >
        {() => (
          <>
            <Form>
              <BussinessAddressComponent
                hasMargin={true}
                isPrimaryAddress={true}
              />
              {isError && <Alert message={errorMessage} />}
              <FooterButton
                nextButtonText={
                  addBussinessAddressLoading ||
                  updateBussinessAddressLoading ||
                  props.disableNextStep
                    ? LOADING_BUTTON_TEXT
                    : NEXT_BUTTON_TEXT
                }
                backButtonText={BACK_BUTTON_TEXT}
                disabled={
                  addBussinessAddressLoading ||
                  updateBussinessAddressLoading ||
                  props.disableNextStep
                }
              />
            </Form>
          </>
        )}
      </Formik>
    </>
  );
};
export default PrimaryBusinessAddressForm;
